import React, {useCallback, useEffect, useMemo, useState} from "react";

import { ColumnsType } from "antd/es/table";
import { Button, Input, message, Space, Table } from "antd";

import {numberValidator} from "../../../../utils/Validator";
import {SafetyData} from "../../../../types/SafetyData";
import {
    addSafetyDataRow, deleteSafetyDataBySid,
    fetchSafetyDataByPid,
    updateSafetyDataRow
} from "../../../../backendInteraction/SafetyDataApiFetch";

interface SafetyDataTableProps {
    data: SafetyData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
}

const SafetyDataTable: React.FC<SafetyDataTableProps> = ( {data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow })=> {
    const [safetyData, setSafetyData] = useState<SafetyData[]>([]);
    const [editingSid, setEditingSid] = useState<number | null>(null);
    const [editData, setEditData] = useState<SafetyData | null>(null);

    useEffect(() => {
        setSafetyData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingSid !== -1 ) {
            const newRow: SafetyData = {
                key: -1,
                sid: -1,
                jha: "",
                sbo: "",
                hazards: "",
                swms_reviews: "",
                observations: "",
                operations_incidents: "",
                incidents: "",
                reportable_incidents: "",
                lost_time_injuries: "",
                medical_treated_injuries: "",
                site_inspections: "",
                comments: "",
                pid: plodId ?? -1,
            }
            setSafetyData(prevData => {
                const hasNewRow = prevData.some(item => item.sid === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            })

            setEditingSid(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingSid]);


    useEffect(() => {
        if (isView) {
            if (editingSid === -1) {
                setSafetyData(prevData => prevData.filter(item => item.sid !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingSid(null);
            setEditData(null);
        }
    }, [isView])


    const saveChanges = useCallback( async () => {
        console.log("Saving changes:", editData);

        if(!editData) return;

        const {  jha, sbo, hazards, swms_reviews, observations, operations_incidents, incidents,
            reportable_incidents, lost_time_injuries, medical_treated_injuries, site_inspections } = editData;

        if(!!jha) {
            const isNumber = numberValidator(jha, 'JHA');
            if (!isNumber) return;
            if (parseFloat(jha) < 0) {
                message.error('The "JHA" value cannot be less than 0.');
                return;
            }
        }

        if(!!sbo) {
            const isNumber = numberValidator(sbo, 'SBO');
            if (!isNumber) return;
            if (parseFloat(sbo) < 0) {
                message.error('The "SBO" value cannot be less than 0.');
                return;
            }
        }

        if(!!hazards) {
            const isNumber = numberValidator(hazards, 'Hazards');
            if (!isNumber) return;
            if (parseFloat(hazards) < 0) {
                message.error('The "Hazards" value cannot be less than 0.');
                return;
            }
        }

        if(!!swms_reviews) {
            const isNumber = numberValidator(swms_reviews, 'SWMS Reviews');
            if (!isNumber) return;
            if (parseFloat(swms_reviews) < 0) {
                message.error('The "SWMS Reviews" value cannot be less than 0.');
                return;
            }
        }

        if(!!observations) {
            const isNumber = numberValidator(observations, 'Observations');
            if (!isNumber) return;
            if (parseFloat(observations) < 0) {
                message.error('The "Observations" value cannot be less than 0.');
                return;
            }
        }

        if(!!operations_incidents) {
            const isNumber = numberValidator(operations_incidents, 'Operations Incidents');
            if (!isNumber) return;
            if (parseFloat(operations_incidents) < 0) {
                message.error('The "Operations Incidents" value cannot be less than 0.');
                return;
            }
        }

        if(!!incidents) {
            const isNumber = numberValidator(incidents, 'Incidents');
            if (!isNumber) return;
            if (parseFloat(incidents) < 0) {
                message.error('The "Incidents" value cannot be less than 0.');
                return;
            }
        }

        if(!!reportable_incidents) {
            const isNumber = numberValidator(reportable_incidents, 'Reportable Incidents');
            if (!isNumber) return;
            if (parseFloat(reportable_incidents) < 0) {
                message.error('The "Reportable Incidents" value cannot be less than 0.');
                return;
            }
        }

        if(!!lost_time_injuries) {
            const isNumber = numberValidator(lost_time_injuries, 'ILTs');
            if (!isNumber) return;
            if (parseFloat(lost_time_injuries) < 0) {
                message.error('The "ILTs" value cannot be less than 0.');
                return;
            }
        }

        if(!!medical_treated_injuries) {
            const isNumber = numberValidator(medical_treated_injuries, 'Medical Injuries');
            if (!isNumber) return;
            if (parseFloat(medical_treated_injuries) < 0) {
                message.error('The "Medical Injuries" value cannot be less than 0.');
                return;
            }
        }

        if(!!site_inspections) {
            const isNumber = numberValidator(site_inspections, 'Site Inspections');
            if (!isNumber) return;
            if (parseFloat(site_inspections) < 0) {
                message.error('The "Site Inspections" value cannot be less than 0.');
                return;
            }
        }
        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingSid === -1) {
            const success = await addSafetyDataRow(editData, aid);
            if (success) {
                setSafetyData( await fetchSafetyDataByPid(plodId ?? -1, aid));
                message.success("New safety data added successfully!");
            } else {
                message.error("Failed to add new safety data.");
            }
        } else {
            const success = await updateSafetyDataRow(editingSid ?? -1, editData);
            if (success) {
                setSafetyData( await fetchSafetyDataByPid(plodId ?? -1, aid));
                message.success("Safety data updated successfully!");
            } else {
                message.error("Failed to update safety data.");
            }
        }

        if (editingSid === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingSid(null);
        setEditData(null);
    }, [editData, editingSid, setIsAddNewRow]);

    const cancelEditing = useCallback((record: SafetyData) => {
        console.log("Cancel editing");
        setEditingSid(null);
        setEditData(null);

        if (record.sid === -1) {
            setSafetyData(prevData => prevData.filter(item => item.sid !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: SafetyData) => {
        console.log("Editing row :", record);
        if ( editingSid === -1 ) {
            setSafetyData(prevData => prevData.filter(item => item.sid !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingSid( prevId => {
            if (prevId !== null) cancelEditing({sid: prevId} as SafetyData);
            return record.sid;
        });

        setEditData({ ...record }) ;
    }, [editingSid, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: SafetyData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteSafetyDataBySid(record.sid);
        if (success) {
            setSafetyData(await fetchSafetyDataByPid(plodId ?? -1, aid));
            message.success("Safety data deleted successfully!");
        } else {
            message.error("Failed to delete safety data.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof SafetyData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);


    const columns: ColumnsType<SafetyData> = useMemo(() => [
        {
            title: "JHA",
            dataIndex: "jha",
            key: "jha",
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.jha}
                        onChange={(e) => handleInputChange("jha", e.target.value)}
                    />
                ) : (
                    record.jha
                ),
        },
        {
            title: "SBO",
            dataIndex: "sbo",
            key: "sbo",
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.sbo}
                        onChange={(e) => handleInputChange("sbo", e.target.value)}
                    />
                ) : (
                    record.sbo
                ),
        },
        {
            title: "Hazards",
            dataIndex: "hazards",
            key: "hazards",
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.hazards}
                        onChange={(e) => handleInputChange("hazards", e.target.value)}
                    />
                ) : (
                    record.hazards
                ),
        },
        {
            title: "SWMS Reviews",
            dataIndex: "swms_reviews",
            key: "swms_reviews",
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.swms_reviews}
                        onChange={(e) => handleInputChange("swms_reviews", e.target.value)}
                    />
                ) : (
                    record.swms_reviews
                ),
        },
        {
            title: 'Observations',
            dataIndex: 'observations',
            key: 'observations',
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.observations}
                        onChange={(e) => handleInputChange("observations", e.target.value)}
                    />
                ) : (
                    record.observations
                ),
        },
        {
            title: 'Operations Incidents',
            dataIndex: 'operations_incidents',
            key: 'operations_incidents',
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.operations_incidents}
                        onChange={(e) => handleInputChange("operations_incidents", e.target.value)}
                    />
                ) : (
                    record.operations_incidents
                ),
        },
        {
            title: 'Incidents',
            dataIndex: 'incidents',
            key: 'incidents',
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.incidents}
                        onChange={(e) => handleInputChange("incidents", e.target.value)}
                    />
                ) : (
                    record.incidents
                ),
        },
        {
            title: 'Reportable Incidents',
            dataIndex: 'reportable_incidents',
            key: 'reportable_incidents',
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.reportable_incidents}
                        onChange={(e) => handleInputChange("reportable_incidents", e.target.value)}
                    />
                ) : (
                    record.reportable_incidents
                ),
        },
        {
            title: 'Lost Time Injuries',
            dataIndex: 'lost_time_injuries',
            key: 'lost_time_injuries',
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.lost_time_injuries}
                        onChange={(e) => handleInputChange("lost_time_injuries", e.target.value)}
                    />
                ) : (
                    record.lost_time_injuries
                ),
        },
        {
            title: 'Medical Treated Injuries',
            dataIndex: 'medical_treated_injuries',
            key: 'medical_treated_injuries',
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.medical_treated_injuries}
                        onChange={(e) => handleInputChange("medical_treated_injuries", e.target.value)}
                    />
                ) : (
                    record.medical_treated_injuries
                ),
        },
        {
            title: 'Site Inspections',
            dataIndex: 'site_inspections',
            key: 'site_inspections',
            width: "7%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.site_inspections}
                        onChange={(e) => handleInputChange("site_inspections", e.target.value)}
                    />
                ) : (
                    record.site_inspections
                ),
        },
        {
            title: 'Comments',
            dataIndex: 'comments',
            key: 'comments',
            width: "13%",
            render: (_, record) =>
                editingSid === record.sid ? (
                    <Input
                        value={editData?.comments}
                        onChange={(e) => handleInputChange("comments", e.target.value)}
                    />
                ) : (
                    record.comments
                ),
        },
        ...(!isView
                ? [{
                    title: 'Action',
                    key: 'action',
                    width: "10%",
                    render: (_: any , record: SafetyData) => (
                        <Space size="small">
                            <Space size="small">
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingSid === record.sid ? saveChanges() : startEditing(record))}>
                                    {editingSid === record.sid ? "Save" : "Edit"}
                                </Button>
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingSid === record.sid ? cancelEditing(record) : deleteRow(record))}>
                                    {editingSid === record.sid ? "Cancel" : "Delete"}
                                </Button>
                            </Space>
                        </Space>
                    ),
                }] as ColumnsType<SafetyData>
                : []
        ),
    ], [isView, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<SafetyData>
            columns={columns}
            pagination={false}
            dataSource={safetyData}
        />
    );
};

export default SafetyDataTable;